// Agent template (#288) — a named, per-account "developer type" the operator
// CRUDs via the control-MCP (`template_{get,set}`, #300) to configure the army.
// A task/role references a template by `name`; the mothership materializes the
// effective template to a BYOVM box at provisioning time, where the runner's
// adapter reads it (`apps/runner/src/adapter/template.ts`) and layers it over
// the box baseline env-config. THIS is the mothership source of truth; the
// runner's on-box `AgentTemplateSchema` is the snake_case FILE mirror.
//
// The three field vocabularies mirror the runner adapter — which domain cannot
// import (apps depend on packages, not the reverse) — so a stored template
// materializes into a runner-parseable file:
//   program    ↔ apps/runner/src/adapter/index.ts        `AdapterProgram` (advisory label)
//   backend    ↔ apps/runner/src/backend/env-profile.ts  `BackendKind`
//   invocation ↔ apps/runner/src/adapter/template.ts     `InvocationOverride`

import { z } from 'zod';
import { AccountIdSchema, TemplateIdSchema } from '../value-objects/ids';

/**
 * Advisory program LABEL — mirrors the runner's `AdapterProgram`. The real
 * behaviour comes from `invocation.command` (a bash command, never hardcoded);
 * this only tags audit/telemetry. `custom` = a fully operator-supplied argv.
 *
 * `native` IS NOT A MEMBER HERE, AND MUST NOT BECOME ONE. The runner's
 * `NATIVE_HARNESS_PROGRAM` docblock (`apps/runner/src/adapter/index.ts`) still
 * reads as an invitation to add it — "making it a real program value needs
 * `AgentProgram` to gain `native` too" — so this is the answer to whoever follows
 * that pointer. Every member of this enum answers WHICH EXTERNAL CLI THE BOX
 * SPAWNS, and the built-in harness spawns nothing: it is a way of DRIVING the
 * model, which is the runner's `AdapterMode`, not a program. A `native` label
 * would also change nothing that runs — this field is advisory by its own first
 * paragraph, while the lane is decided by `mode`. A template selects the built-in
 * harness through the runner template's `mode` field
 * (`apps/runner/src/adapter/template.ts`); the fifth label would only give an
 * operator a second, inert way to ask for it.
 */
export const AgentProgram = {
  Codex: 'codex',
  Claude: 'claude',
  Custom: 'custom',
} as const;
export const AgentProgramSchema = z.enum(AgentProgram);
export type AgentProgram = z.infer<typeof AgentProgramSchema>;

/**
 * Inference backend kind — mirrors the runner's `BackendKind` (env-profile).
 * `none` = the box manages `ANTHROPIC_*` itself (no injection).
 *
 * The mirror is the whole contract: a value the runner accepts but this enum
 * omits is a template an operator cannot author for a box the runner can run.
 * `openai-compatible` was exactly that omission (#1313) — and not a niche one,
 * since `PROVIDER_WIRE` puts every provider outside the Anthropic-wire minority
 * on the OpenAI wire. (Stated as a property, not a roster: the roster was
 * `anthropic` and `zai` until #1904 added `router` as a third.) The guard has
 * to live on the RUNNER side (`backend/env-profile.test.ts`)
 * — domain cannot import an app — but it pins the two lists together so the next
 * backend kind cannot land on one side only.
 */
export const AgentBackend = {
  None: 'none',
  Anthropic: 'anthropic',
  AnthropicCompatible: 'anthropic-compatible',
  OpenAICompatible: 'openai-compatible',
} as const;
export const AgentBackendSchema = z.enum(AgentBackend);
export type AgentBackend = z.infer<typeof AgentBackendSchema>;

/**
 * Invocation overrides — mirrors the runner's `InvocationOverride`. Every field
 * is optional and carries NO Zod default: an unset field must fall through to
 * the box baseline at merge time (a default here would incorrectly "win" over
 * the baseline — same reason the runner keeps a defaults-free override schema).
 */
export const TemplateInvocationSchema = z.object({
  /** The executable (or shell wrapper). Replaces the baseline command when set. */
  command: z.string().min(1).max(500).optional(),
  /** Fixed argv preceding any appended prompt. */
  args: z.array(z.string().max(10_000)).max(100).optional(),
  /** How the rendered brief reaches the program. */
  promptVia: z.enum(['stdin', 'arg', 'none']).optional(),
  /**
   * Extra env merged over the baseline box env (template keys win) — EXCEPT the
   * reserved names, refused at both write edges (`templateReservedEnvRefusal`,
   * `@developerz/mcp`) and dropped at the runner merge (#1894): never a
   * credential, never the inference/git-auth vars, never `DZ_SYSTEM_PROMPT`
   * (the immutable prompt floor). Never BYOK.
   */
  env: z.record(z.string(), z.string()).optional(),
  /** Working directory (the repo checkout on the VM). */
  cwd: z.string().min(1).optional(),
  /** Hard per-run ceiling (ms), capped at 24h. */
  timeoutMs: z
    .number()
    .int()
    .positive()
    .max(24 * 60 * 60_000)
    .optional(),
});
export type TemplateInvocation = z.infer<typeof TemplateInvocationSchema>;

/**
 * One `agent_templates` row. Parsed on the way OUT of the DB (`normalizeTemplate`)
 * so a drifted write — bad id prefix, unknown program/backend, malformed
 * invocation — fails loudly at read, the same parse gate `tasks`/`runners` use.
 */
export const AgentTemplateSchema = z
  .object({
    /** `tpl_<hex>` — the branded id IS the stored text PK (same scheme as tasks/accounts). */
    id: TemplateIdSchema,
    accountId: AccountIdSchema,
    /** Human handle, unique per account — how a task/role references this template + the MCP upsert key. */
    name: z.string().min(1).max(100),
    /** Advisory program label; `custom` at the column default. */
    program: AgentProgramSchema,
    /** Invocation overrides merged over the box baseline; null = no overrides. */
    invocation: TemplateInvocationSchema.nullable(),
    /** Backend kind for the box env-profile; `none` at the column default. */
    backend: AgentBackendSchema,
    /**
     * Endpoint for a COMPATIBLE backend (`DZ_BACKEND_BASE_URL`, #1351); null for
     * `none` / direct `anthropic`, which resolve with no endpoint at all.
     *
     * The pairing rule is the refinement below, not the field: a compatible kind
     * with no endpoint describes a box that cannot boot, and before this the
     * template had no field to complete one — so `template set` accepted a value
     * whose only effect was a box exiting at startup.
     *
     * No TOKEN field, deliberately. A template is provisioned state that
     * materializes to a JSON file on the box; the credential stays a box secret
     * (`DZ_BACKEND_TOKEN` via `loadSecret`). Endpoint is config, token is not.
     */
    baseUrl: z.url().max(2_000).nullable(),
    /** Model id forwarded to the coding agent (`DZ_MODEL_OVERRIDE`); null = box default. */
    model: z.string().min(1).max(200).nullable(),
    /** System prompt injected into the agent (`DZ_SYSTEM_PROMPT`); null = none. */
    systemPrompt: z.string().min(1).max(50_000).nullable(),
    /** Knowledge-base scope reference (`DZ_KB_REF`); null = unscoped. */
    kbRef: z.string().min(1).max(200).nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
  })
  // A COMPATIBLE backend has no default endpoint, so a template naming one with
  // no `baseUrl` describes a box that cannot boot (#1351). Refusing at the
  // authoring gate keeps the failure with the operator writing the template,
  // instead of on a box exiting at startup naming an env var they never saw.
  .refine((t) => !agentBackendNeedsBaseUrl(t.backend) || t.baseUrl !== null, {
    message:
      'a compatible backend requires baseUrl (anthropic-compatible / openai-compatible have no default endpoint)',
    path: ['baseUrl'],
  });
export type AgentTemplate = z.infer<typeof AgentTemplateSchema>;

/**
 * Which backend kinds cannot resolve without an explicit endpoint (#1351).
 * Mirrors the runner's `backendNeedsBaseUrl`; the pairing rule has to be stated
 * on BOTH ends because each parses a different shape of the same template.
 */
export function agentBackendNeedsBaseUrl(kind: AgentBackend): boolean {
  return kind === AgentBackend.AnthropicCompatible || kind === AgentBackend.OpenAICompatible;
}
